import {useEffect, useMemo, useRef} from 'react';

import {LARGE_PROJECT_RUN_THRESHOLD} from '../../../agentChat/pageContext';
import {
  useProjectLastActiveQuery,
  useProjectRunsCountQuery,
  useProjectTotalRunsQuery,
  useSweepRunsCountQuery,
} from '../../../generated/graphql';
import {useProjectParams} from '../../../pages/ProjectPage/useProjectParams';
import {type ApolloError} from '../../../state/graphql/apolloWithAbort';
import {useSyncedPollingTick} from '../../../state/polling/hooks';
import {usePinnedRuns} from '../RunSelectorRunsContext';
import {isCrossProjectRun} from '../util';

export interface RunsCountResult {
  count: number;
  loading: boolean;
  error: ApolloError | undefined;
  skipped: boolean;
  /**
   * True when runs have likely been logged since `count` was fetched, so the
   * count may be stale. Only set on large projects, where the exact count isn't
   * polled; callers use it to show a "count may be out of date" hint. Always
   * false on small projects (the count is polled and stays fresh).
   */
  isCountStale: boolean;
}

/**
 * @returns The number of cross-project pinned runs.
 */
export function useNumCrossProjectRunsCount(): number {
  const {entityName, projectName} = useProjectParams();
  const pinnedRuns = usePinnedRuns();
  return useMemo(() => {
    if (!entityName || !projectName || !pinnedRuns) {
      return 0;
    }
    return pinnedRuns.filter(r =>
      isCrossProjectRun(r.project, {entityName, name: projectName})
    ).length;
  }, [pinnedRuns, entityName, projectName]);
}

/**
 * @returns Whether the current project is "large" per LARGE_PROJECT_RUN_THRESHOLD.
 *
 * Reads the cached `totalRuns` field (not the uncached `runCount`), so this is
 * a cheap size classification with no aggregation cost. While the size is still
 * unknown (the query is in flight), we assume large so callers don't fire an
 * expensive visibility aggregation on a project that turns out to be large; the
 * cached query usually resolves immediately. When there is no project to query
 * (skipped), we default to not-large.
 */
export function useIsLargeProject(): boolean {
  const {entityName, projectName} = useProjectParams();
  const skip = !entityName || !projectName;
  const query = useProjectTotalRunsQuery({
    variables: {entityName: entityName ?? '', projectName: projectName ?? ''},
    skip,
  });
  const totalRuns = query.data?.project?.totalRuns;
  if (totalRuns == null) {
    return !skip;
  }
  return totalRuns >= LARGE_PROJECT_RUN_THRESHOLD;
}

/**
 * @returns Query result for a project's total run count.
 *
 * Does not include filters, search, or cross-project pinned runs.
 */
export function useProjectRunsCount({
  skip = false,
}: {
  skip?: boolean;
} = {}): RunsCountResult {
  const {entityName, projectName} = useProjectParams();
  const skipped = skip || !entityName || !projectName;

  const query = useProjectRunsCountQuery({
    variables: {entityName: entityName ?? '', projectName: projectName ?? ''},
    skip: skipped,
  });

  const isLargeProject = useIsLargeProject();

  useRefetchCountOnPollTick(query.refetch, {
    enabled: !skipped && !isLargeProject,
    loading: query.loading,
  });

  const isCountStale = useIsCountStaleSinceLoad({
    entityName,
    projectName,
    enabled: !skipped && isLargeProject,
  });

  return {
    count: query.data?.project?.runCount ?? 0,
    loading: query.loading && query.data == null,
    error: query.error,
    skipped,
    isCountStale,
  };
}

/**
 * @returns Query result for a sweep's total run count.
 *
 * Does not include filters, search, or cross-project pinned runs.
 */
export function useSweepRunsCount({
  skip = false,
}: {
  skip?: boolean;
} = {}): RunsCountResult {
  const {entityName, projectName, sweepName} = useProjectParams();
  const skipped = skip || !entityName || !projectName || sweepName == null;

  const query = useSweepRunsCountQuery({
    variables: {
      entityName: entityName ?? '',
      projectName: projectName ?? '',
      sweepName: sweepName ?? '',
    },
    skip: skipped,
  });

  const isLargeProject = useIsLargeProject();
  useRefetchCountOnPollTick(query.refetch, {
    enabled: !skipped && !isLargeProject,
    loading: query.loading,
  });

  return {
    count: query.data?.project?.sweep?.runCount ?? 0,
    loading: query.loading && query.data == null,
    error: query.error,
    skipped,
    isCountStale: false,
  };
}

/**
 * @returns Whether new runs have likely been logged since the project run count
 * loaded, on large projects only (false otherwise).
 */
export function useIsProjectRunCountStale(): boolean {
  const {entityName, projectName} = useProjectParams();
  const isLargeProject = useIsLargeProject();
  const skipped = !entityName || !projectName;
  return useIsCountStaleSinceLoad({
    entityName,
    projectName,
    enabled: !skipped && isLargeProject,
  });
}

/**
 * useRefetchCountOnPollTick refetches the project's run count on each synced poll
 * tick so the total count tracks runs logged after the initial fetch
 */
function useRefetchCountOnPollTick(
  refetch: () => void,
  {enabled, loading}: {enabled: boolean; loading: boolean}
): void {
  // useSyncedPollingTick (not useSyncedPollingSubscriber) so a tick doesn't
  // re-render the counter; we only want to kick off the refetch.
  useSyncedPollingTick('RunsCounter', () => {
    if (enabled && !loading) {
      refetch();
    }
  });
}

function toMs(iso: string | null | undefined): number | null {
  if (iso == null) {
    return null;
  }
  const ms = new Date(iso).getTime();
  return Number.isNaN(ms) ? null : ms;
}

/**
 * Cheaply detects if there are likely new runs since the exact count loaded. Used for large
 * projects so we can display that the count is stale without polling for run count
 *
 * NOTE: `lastActive` moves on any run activity (~2-minute granularity), not
 * only run creation, so this may be delayed.
 */
function useIsCountStaleSinceLoad({
  entityName,
  projectName,
  enabled,
}: {
  entityName: string | undefined;
  projectName: string | undefined;
  enabled: boolean;
}): boolean {
  const query = useProjectLastActiveQuery({
    variables: {entityName: entityName ?? '', projectName: projectName ?? ''},
    skip: !enabled,
    // Fetch fresh so the anchor isn't seeded from a stale cached `lastActive`
    // (populated by other queries), which would flip the hint on with no new
    // activity. Both anchor and current value must come from real network reads.
    fetchPolicy: 'network-only',
  });

  const {refetch, loading} = query;
  // useSyncedPollingTick (not useSyncedPollingSubscriber) so a tick doesn't
  // re-render this hook's consumers; we only want to kick off the refetch.
  useSyncedPollingTick('RunsCountActivity', () => {
    if (enabled && !loading) {
      refetch();
    }
  });

  const currentMs = toMs(query.data?.project?.lastActive);

  // The anchor is the activity timestamp as of the first count load. We keep it
  // in a ref so a `lastActive` poll can't move it (both queries share the same
  // normalized `Project.lastActive`, so reading it live would drag the anchor
  // along and the hint would never fire).
  const anchorRef = useRef<number | null>(null);

  // Reset the anchor when the project changes so we never compare a new
  // project's lastActive against a previous project's anchor. This runs during
  // render (React's "adjust state when a prop changes" pattern) so the reset
  // lands before isCountStale is computed below; doing it in an effect would be
  // a render too late. The current consumers remount per project, but this keeps
  // future consumers safe.
  const projectKey = `${entityName ?? ''}/${projectName ?? ''}`;
  const prevProjectKeyRef = useRef(projectKey);
  if (prevProjectKeyRef.current !== projectKey) {
    prevProjectKeyRef.current = projectKey;
    anchorRef.current = null;
  }

  useEffect(() => {
    if (!enabled) {
      anchorRef.current = null;
      return;
    }
    // Seed the anchor on the first observed activity so the hint only reflects
    // activity that happens after this count was loaded.
    if (anchorRef.current == null && currentMs != null) {
      anchorRef.current = currentMs;
    }
  }, [enabled, currentMs]);

  return (
    enabled &&
    anchorRef.current != null &&
    currentMs != null &&
    currentMs > anchorRef.current
  );
}
